import React, { useEffect, useState } from "react"
import { Typography, Paper, Box, Grid } from "@mui/material"
import { useParams, useNavigate } from "react-router-dom"
import { useAuthUser } from "react-auth-kit"
import api from "../../utils/api"

interface Question {
  id: string
  description: string
  questionType: "Single" | "Ranked"
  optionCount: number
  questionNum: number
}

interface Option {
  id: string
  option: string
  questionId: string
  optionNum: number
}

function VoterResultsPage() {
  const { id } = useParams<{ id: string }>()
  const auth = useAuthUser()
  const [questions, setQuestions] = useState<Question[]>([])
  const [answers, setAnswers] = useState<Option[]>([])
  const [voteCounts, setVoteCounts] = useState<any>({})
  const [voterData, setVoterData] = useState<any>({})
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { id: instanceId } = useParams<{ id: string }>()
  const voterId = auth()?.id
  const eventId = auth()?.eventId
  const navigate = useNavigate()

  ///This is what was used to restrict access to pages

  // all instance ids from eventId
  useEffect(() => {
    if (eventId) {
      api
        .get(`/admin/events/${eventId}/voting-instances-ids`)
        .then((response) => {
          const instances = response.data
          const exists = instances.some((instance: any) => instance.id === id)
          if (!exists) {
            navigate(
              "/not-authorized?message=The%20instance%20does%20not%20exist"
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })

      api
        .get(`/admin/instances/${id}/status`)
        .then((response) => {
          const status = response.data.status
          if (status !== "published") {
            navigate(
              "/not-authorized?message=The%20instance%20is%20not%20published"
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })
    }
  }, [eventId, id, navigate])
  //end of code

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    // Fetch instance details first to check status
    api.get(`/admin/instances/${instanceId}`)
      .then((response) => {
        const instanceData = response.data;
        if (instanceData.status !== 'published') {
          setError('Results have not been published yet. Please check back later.');
          setIsLoading(false);
          return Promise.reject('Instance not published');
        }

        // Use Promise.all to fetch both instance details and votes in parallel
        return Promise.all([
          api.get(`/admin/instances/${instanceId}/details`),
          api.get(`/admin/instances/${instanceId}/results`)
        ]);
      })
      .then(([detailsRes, votesRes]) => {
        console.log('Details data:', detailsRes.data);
        // Set questions and answers from the details response
        setQuestions(detailsRes.data.questions || []);
        setAnswers(detailsRes.data.answers || []);
        
        const { voteCounts } = votesRes.data;
        setVoteCounts(voteCounts || {});
        console.log('Vote counts:', voteCounts);
      })
      .catch(error => {
        console.error("Error:", error);
        if (!error || error !== 'Instance not published') {
          setError('Failed to load voting results. Please try again later.');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [instanceId])

  return (
    <div>
      <Typography variant="h4" align="center" gutterBottom>
        Voter Results
      </Typography>
      {isLoading ? (
        <Typography variant="h6" align="center" gutterBottom>
          Loading results...
        </Typography>
      ) : error ? (
        <Typography variant="h6" align="center" color="error" gutterBottom>
          {error}
        </Typography>
      ) : questions && questions.length > 0 ? (
        questions.map((question) => {
          // Filter options for this specific question
          const questionOptions = answers.filter(
            (answer) => answer.questionId === question.id
          );

          // Calculate max votes for this question
          const questionVotes = voteCounts?.[question.id] || {};
          const maxVotes = Object.values(questionVotes).length > 0
            ? Math.max(...Object.values(questionVotes).map(count => count as number))
            : 0;

          return (
            <Paper
              key={question.id}
              style={{ margin: "20px 0", padding: "20px" }}
            >
              <Typography variant="h6" align="center" gutterBottom>
                {question.description}
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                {questionOptions.map((option) => {
                  const voteCount = questionVotes[option.id] || 0;
                  const isMaxVotes = voteCount === maxVotes && maxVotes > 0;
                  const bgColor = isMaxVotes ? "green" : "grey";

                  return (
                    <Grid item key={option.id} xs={12} sm={6} md={4} lg={3}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                        bgcolor={bgColor}
                        borderRadius={2}
                        width="100%"
                        height="120px"
                        color="white"
                      >
                        <Typography variant="subtitle1" align="center" gutterBottom>
                          {option.option}
                        </Typography>
                        <Typography variant="h6" align="center">
                          {voteCount} {voteCount === 1 ? "vote" : "votes"}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          );
        })
      ) : (
        <Typography variant="h6" align="center" gutterBottom>
          No questions available.
        </Typography>
      )}
    </div>
  )
}

export default VoterResultsPage
